// @flow
import React from 'react';
import { Helmet } from 'react-helmet';
import { isBrowser } from 'react-device-detect';
import { FaFacebookSquare, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { MdEmail, MdLink } from 'react-icons/md';
import Button from 'components/common/Button';
import { type Profile } from 'store/modules/profile';
import { resizeImage } from 'lib/common';

import './UserHead.scss';

type Props = {
  username: string,
  profile: Profile,
  self: boolean,
  following: ?boolean,
  onToggleFollow: () => void,
  rawTagName: ?string,
  type: string,
};

const UserHead = ({
  username,
  profile,
  self,
  following,
  onToggleFollow,
  rawTagName,
  type,
}: Props) => {
  const title = `${profile.display_name} (${username})${rawTagName ? ` #${rawTagName}` : ''}`;
  const { linkedin, twitter, facebook, email, url } = profile.profile_links;
  const hasAccount = !!(linkedin || twitter || facebook);
  const hasLink = !!(url || email);
  const userUrl = `${process.env.BASE_URL}/@${username}/${type}`;
  const re = new RegExp('^(http|https)://', 'i');
  const match = re.test(url);
  const myUrl = match ? url : `http://${url}`;

  return (
    <div className="UserHead">
      <Helmet>
        <title>{`${title} | 캐시바이`}</title>
        <meta name="description" content={profile.short_bio} />
        <meta property="og:url" content={userUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${title} | 캐시바이`} />
        <meta property="og:description" content={profile.short_bio} />
        {profile.thumbnail && <meta property="og:image" content={profile.thumbnail} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} | 캐시바이`} />
        <meta name="twitter:description" content={profile.short_bio} />
        {profile.thumbnail && <meta name="twitter:image" content={profile.thumbnail} />}
      </Helmet>
      <img
        src={resizeImage(
          profile.thumbnail || 'https://images.cacheby.com/default_thumbnail.png',
          150,
          150,
        )}
        alt="thumbnail"
      />
      <div className="user-info">
        <section className="top">
          {!self && following !== undefined && (
            <div className="subscribe-wrapper">
              {following ? (
                <Button
                  className="subscribe"
                  theme="gray"
                  onClick={onToggleFollow}
                  desktop={isBrowser}
                >
                  팔로잉
                </Button>
              ) : (
                <Button className="subscribe" onClick={onToggleFollow} desktop={isBrowser}>
                  팔로우
                </Button>
              )}
            </div>
          )}
          <div className="username">{username}</div>
        </section>
        <section className="profile-content">
          <h2 className="title">{profile.display_name}</h2>
          <p>{profile.short_bio}</p>
          <div className="social-info">
            {hasAccount && (
              <div className="icons">
                {linkedin && (
                  <a href={`https://linkedin.com/${linkedin}`} target="_blank">
                    <FaLinkedin />
                  </a>
                )}
                {twitter && (
                  <a href={`https://twitter.com/${twitter}`} target="_blank">
                    <FaTwitter />
                  </a>
                )}
                {facebook && (
                  <a href={`https://facebook.com/${facebook}`} target="_blank">
                    <FaFacebookSquare />
                  </a>
                )}
              </div>
            )}
            {hasLink && (
              <div className="other-links">
                {email && (
                  <div className="link-line">
                    <MdEmail />
                    <a href={`mailto:${email}`}>{email}</a>
                  </div>
                )}
                {url && (
                  <div className="link-line">
                    <MdLink />
                    <a href={myUrl} target="_blank">
                      {myUrl}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

UserHead.Placeholder = () => (
  <div className="UserHead placeholder">
    <div className="fake-thumbnail" />
    <div className="user-info">
      <section className="top">
        <div className="username">
          <div className="gray-block _username" />
        </div>
      </section>
      <section className="mini-profile">
        <div className="gray-block _name" />
        <div className="gray-block _description" />
      </section>
    </div>
  </div>
);

export default UserHead;



// WEBPACK FOOTER //
// ./src/components/user/UserHead/UserHead.js